import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaStar, FaCheckCircle, FaTimesCircle, FaHotel, FaPaw } from "react-icons/fa";
import "../css/styles.css";

const SerengetiMigration = () => {
  return (
    <div className="safari-detail-page">
      {/* ===== HERO ===== */}
      <section className="safari-detail-hero">
        <div className="safari-detail-hero-bg">
          <img
            src="https://res.cloudinary.com/dn39bkkiq/image/upload/q_auto,f_auto,w_1400/v1776348629/6_Day_Serengeti_Migration_Safari_mkyfhv.jpg"
            alt="Serengeti Migration Safari"
          />
          <div className="safari-detail-hero-overlay" />
        </div>
        <div className="safari-detail-hero-content">
          <span className="safari-detail-eyebrow">Tanzania Safari</span>
          <h1>6 Day Serengeti Migration Safari</h1>
          <p className="safari-detail-hero-sub">
            Witness one of the greatest wildlife spectacles on Earth — the Great Wildebeest Migration.
          </p>
          <div className="safari-detail-hero-buttons">
            <Link to="/contact" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== OVERVIEW ===== */}
      <section className="safari-detail-overview">
        <div className="safari-detail-section-header">
          <span className="section-tag">Safari Overview</span>
          <h2>Into the Heart of the Migration</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-text">
          <p>
            The Great Wildebeest Migration is one of the greatest wildlife spectacles on Earth, and this safari is designed to bring you right into the heart of it. Each year, millions of wildebeest, zebras, and gazelles move across the plains of Serengeti National Park in search of fresh grass and water.
          </p>
          <p>
            During this journey, you will explore the Serengeti's vast landscapes while following the movement of these great herds. Lions, cheetahs, and hyenas are never far away, making every game drive full of anticipation and excitement.
          </p>
          <p>
            The adventure continues with a visit to the breathtaking Ngorongoro Crater, where wildlife thrives inside an ancient volcanic caldera.
          </p>
        </div>
      </section>

      {/* ===== HIGHLIGHTS ===== */}
      <section className="safari-detail-highlights">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Highlights</span>
          <h2>Safari Highlights</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-highlights-list">
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Witness the spectacular Great Wildebeest Migration (seasonal)</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Explore the legendary plains of Serengeti National Park</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Exceptional opportunities to see predators such as lions and cheetahs</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Descend into the wildlife-rich Ngorongoro Crater</p>
          </div>
          <div className="safari-detail-highlight-item">
            <FaStar className="safari-detail-highlight-icon" />
            <p>Optional sunrise hot air balloon safari over the Serengeti</p>
          </div>
        </div>
      </section>

      {/* ===== AT A GLANCE ===== */}
      <section className="safari-detail-glance">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Quick Info</span>
          <h2>Safari at a Glance</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-glance-grid">
          <div className="safari-detail-glance-card">
            <FaClock className="safari-detail-glance-icon" />
            <h4>Duration</h4>
            <p>6 Days / 5 Nights</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Start / End</h4>
            <p>Arusha</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaMapMarkerAlt className="safari-detail-glance-icon" />
            <h4>Destinations</h4>
            <p>Serengeti &ndash; Ngorongoro</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaCalendarAlt className="safari-detail-glance-icon" />
            <h4>Best Time to Travel</h4>
            <p>June &ndash; October</p>
          </div>
          <div className="safari-detail-glance-card">
            <FaPaw className="safari-detail-glance-icon" />
            <h4>Safari Style</h4>
            <p>Migration &amp; Big Game</p>
          </div>
        </div>
      </section>

      {/* ===== ITINERARY ===== */}
      <section className="safari-detail-itinerary">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Day by Day</span>
          <h2>Day-by-Day Itinerary</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-days">
          {/* Day 1 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">1</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 1 — Arrival in Arusha</h3>
              <p>
                Arrive at Kilimanjaro International Airport, where your Nyatwende representative will warmly welcome you to Tanzania. You will then transfer to your lodge in Arusha, a peaceful town surrounded by green hills and coffee plantations.
              </p>
              <p>
                Spend the rest of the day relaxing after your journey, enjoying the calm atmosphere and preparing for the adventure ahead.
              </p>
              <p className="safari-detail-day-note">Overnight in Arusha.</p>
            </div>
          </div>

          {/* Day 2 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">2</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 2 — Arusha to Serengeti National Park</h3>
              <p>
                After breakfast, begin your journey toward the world-famous Serengeti National Park. Depending on the safari plan, you will either enjoy a scenic drive through the Ngorongoro highlands or take a short flight directly into the Serengeti.
              </p>
              <p>
                Upon arrival, enjoy your first game drive across the endless plains. The Serengeti is one of Africa's most spectacular wildlife destinations, home to lions, cheetahs, elephants, giraffes, and countless antelope.
              </p>
              <p>
                Arrive at your lodge in time for dinner as the African sunset paints the sky in warm golden colors.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Day 3 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">3</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 3 — Full Day in Serengeti (Migration Tracking)</h3>
              <p>
                Spend the day exploring the vast Serengeti plains in search of the Great Wildebeest Migration, one of nature's most extraordinary wildlife events.
              </p>
              <p>
                Millions of wildebeest, zebras, and gazelles move across the Serengeti ecosystem in search of fresh grazing. Following them are predators such as lions, cheetahs, and hyenas, creating dramatic wildlife encounters.
              </p>
              <p>
                Morning and afternoon game drives offer incredible opportunities for photography and unforgettable sightings.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Day 4 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">4</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 4 — Full Day in Serengeti</h3>
              <p>
                Another full day is dedicated to exploring the Serengeti's diverse landscapes. Your guide will take you to areas where wildlife activity is strongest depending on the season.
              </p>
              <p>
                Optional experience: a sunrise hot air balloon safari, where you drift quietly above the plains as the wildlife awakens below. After landing, enjoy a special bush breakfast in the wilderness.
              </p>
              <p>
                Return to the lodge for relaxation and dinner under the African stars.
              </p>
              <p className="safari-detail-day-note">Overnight in the Serengeti.</p>
            </div>
          </div>

          {/* Day 5 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">5</div>
              <div className="safari-detail-day-line" />
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 5 — Serengeti to Ngorongoro Conservation Area</h3>
              <p>
                After breakfast, enjoy a final morning game drive before departing the Serengeti and heading toward the Ngorongoro Conservation Area.
              </p>
              <p>
                The drive takes you through beautiful highland landscapes and Maasai villages before reaching the crater rim, where breathtaking views stretch across the world's largest intact volcanic caldera.
              </p>
              <p>
                Spend the evening relaxing at your lodge overlooking this natural wonder.
              </p>
              <p className="safari-detail-day-note">Overnight in Ngorongoro.</p>
            </div>
          </div>

          {/* Day 6 */}
          <div className="safari-detail-day">
            <div className="safari-detail-day-marker">
              <div className="safari-detail-day-number">6</div>
            </div>
            <div className="safari-detail-day-content">
              <h3>Day 6 — Ngorongoro Crater Safari &amp; Return to Arusha</h3>
              <p>
                After an early breakfast, descend into the Ngorongoro Crater for a morning game drive. The crater floor is home to an incredible density of wildlife, including elephants, lions, buffalo, zebras, and sometimes even the rare black rhino.
              </p>
              <p>
                Enjoy a picnic lunch inside the crater surrounded by stunning scenery.
              </p>
              <p>
                In the afternoon, drive back to Arusha, where you will be transferred to Kilimanjaro International Airport for your onward journey.
              </p>
              <p className="safari-detail-day-note">
                Your safari ends here, leaving you with unforgettable memories of the Serengeti and the great migration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== ACCOMMODATION ===== */}
      <section className="safari-detail-accommodation">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">Where You'll Stay</span>
          <h2>Accommodation Examples</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-accom-grid">
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier midrange">Arusha</div>
            <ul>
              <li><FaHotel /> Arusha Serena Hotel</li>
              <li><FaHotel /> Gran Meli&aacute; Arusha</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Serengeti</div>
            <ul>
              <li><FaHotel /> Serengeti Serena Safari Lodge</li>
              <li><FaHotel /> Serengeti Simba Lodge</li>
            </ul>
          </div>
          <div className="safari-detail-accom-card">
            <div className="safari-detail-accom-tier luxury">Ngorongoro</div>
            <ul>
              <li><FaHotel /> Ngorongoro Serena Safari Lodge</li>
              <li><FaHotel /> Ngorongoro Sopa Lodge</li>
            </ul>
          </div>
        </div>
        <p className="safari-detail-accom-note">
          All lodges are carefully selected for comfort, excellent locations, and authentic safari experiences. Similar lodges of the same standard may be used depending on availability.
        </p>
      </section>

      {/* ===== INCLUDED / EXCLUDED ===== */}
      <section className="safari-detail-inclusions">
        <div className="safari-detail-section-header">
          <span className="section-tag centered">What to Expect</span>
          <h2>What's Included</h2>
          <div className="safari-detail-line" />
        </div>
        <div className="safari-detail-inclusions-grid">
          <div className="safari-detail-inclusions-card included">
            <h3><FaCheckCircle /> Included</h3>
            <ul>
              <li>Airport transfers in Arusha</li>
              <li>Transport in a safari vehicle with professional driver-guide</li>
              <li>Park entrance fees</li>
              <li>Accommodation during the safari</li>
              <li>Meals as specified in the itinerary</li>
              <li>Bottled drinking water during game drives</li>
            </ul>
          </div>
          <div className="safari-detail-inclusions-card excluded">
            <h3><FaTimesCircle /> Not Included</h3>
            <ul>
              <li>International flights</li>
              <li>Travel insurance</li>
              <li>Visa fees</li>
              <li>Optional activities such as hot air balloon safari</li>
              <li>Personal expenses</li>
              <li>Tips and gratuities</li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="safari-detail-cta">
        <div className="safari-detail-cta-content">
          <h2>Customize This Safari</h2>
          <p>
            This migration safari can easily be tailored to match your travel style. Add extra nights in the Serengeti to follow the migration, upgrade to luxury tented camps in prime wildlife locations, or extend your journey with a Zanzibar beach escape.
          </p>
          <div className="safari-detail-cta-buttons">
            <Link to="/contact?safari=6 Day Serengeti Migration Safari" className="hero-cta primary">
              Plan Your Safari <FaArrowRight className="btn-icon" />
            </Link>
            <Link to="/tours" className="hero-cta secondary">
              View More Safaris
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SerengetiMigration;
